import React, { CSSProperties, ReactNode, useCallback, useMemo } from 'react';

import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';

import useBetaMode from '../../../hooks/helper/useBetaMode';
//hooks
import useHandleQueryParams from '../../../hooks/helper/useHandleQueryParams';

// models
import { AampeStepperStepProps } from '../../../models/stepper.model';

// components
import BasicButton from '../../buttons/basicButton';
import Divider, { DividerType } from '../Divider';
import AampeSteps from './AampeSteps';

interface StyledProps {
    isVisible?: boolean;
    isCoverStep?: boolean;
}
interface AampeStepperComponentProps {
    steps: AampeStepperStepProps[];
    rightActionButtonsForLastStep?: React.ReactElement;
    showStepsAsCompleted?: boolean;
    className?: string;
    dividerType?: DividerType;
    currentTab?: string;
    setNewTab?: (value: string) => void;
    disableAllAction?: boolean;
    contentContainerStyle?: CSSProperties;
    containerId?: string;
    customNextButtonStyle?: CSSProperties;
    customBackButtonStyle?: CSSProperties;
    containerStyle?: CSSProperties;
    customStepperBodyHeader?: ReactNode;
    stepsStyle?: CSSProperties;
    actionsInHeader?: boolean;
    onCancel?: () => void;
    cancelLabel?: string;
    dividerWidth?: number | string;
}

const AampeStepperComponent: React.FC<
    React.PropsWithChildren<AampeStepperComponentProps>
> = ({
    steps,
    rightActionButtonsForLastStep,
    showStepsAsCompleted,
    className,
    dividerType = 'horizontal',
    currentTab,
    setNewTab,
    disableAllAction,
    contentContainerStyle,
    containerId,
    customNextButtonStyle,
    customBackButtonStyle,
    containerStyle,
    customStepperBodyHeader,
    stepsStyle,
    actionsInHeader = false,
    onCancel,
    cancelLabel = 'Cancel',
    dividerWidth,
}) => {
    const { active: isBetaModeActive } = useBetaMode();
    const [queryParams, setQueryParams] = useHandleQueryParams();
    const current_step = queryParams.current_step;

    const mappedSteps = useMemo<AampeStepperStepProps[]>(() => {
        if (isBetaModeActive) {
            return steps;
        }
        return steps.filter((item) => !item.isBetaMode);
    }, [isBetaModeActive, steps]);

    const stepIndex = useMemo<number>(() => {
        const currentStepIndex = mappedSteps.findIndex(
            (step) =>
                step.tabKey === currentTab || step.tabKey === current_step,
        );
        if (currentStepIndex === -1) {
            return 0;
        } else {
            return currentStepIndex;
        }
    }, [currentTab, mappedSteps, current_step]);

    const isCoverStep = useMemo<boolean>(() => {
        return mappedSteps[stepIndex].title === 'Cover';
    }, [mappedSteps, stepIndex]);

    const handleNext = useCallback(async () => {
        const nextStep = mappedSteps[stepIndex + 1];
        const currentStep = mappedSteps[stepIndex];

        if (!nextStep) {
            console.warn('No next step available.');
            return;
        }
        const handler = currentStep.nextButtonProps?.handler;

        if (handler) {
            // Await because we want to stay on current tab if handler fails
            const success = await handler();
            if (success === false) {
                return;
            }
        }

        if (setNewTab) {
            setNewTab(nextStep.tabKey);
        }

        setQueryParams({ current_step: nextStep.tabKey });
    }, [stepIndex, setNewTab, mappedSteps, setQueryParams]);

    const handleBack = useCallback(() => {
        const prevStep = mappedSteps[stepIndex - 1];
        if (setNewTab) {
            setNewTab(prevStep.tabKey);
        }

        setQueryParams({ current_step: prevStep?.tabKey });
    }, [mappedSteps, stepIndex, setNewTab, setQueryParams]);

    const handleStepClick = useCallback(
        (index: number) => {
            if (!mappedSteps[index]?.disableStepInfo?.isDisabled) {
                const step = mappedSteps[index];
                if (setNewTab) {
                    setNewTab(step.tabKey);
                }
                setQueryParams({ current_step: step.tabKey });
            }
        },
        [mappedSteps, setNewTab, setQueryParams],
    );

    const isNextButtonDisabled = useMemo<boolean>(() => {
        return mappedSteps[stepIndex].nextButtonProps?.isDisabled || false;
    }, [mappedSteps, stepIndex]);

    const nextButtonLabel =
        mappedSteps[stepIndex].nextButtonProps?.label ?? 'Next';

    const headerActionButtons = actionsInHeader &&
        !mappedSteps[stepIndex].hideActionButtons && (
            <HeaderActionContainer>
                {onCancel && (
                    <BasicButton
                        isPrimary={false}
                        name={cancelLabel}
                        isOutlined
                        action={onCancel}
                    />
                )}
                {!onCancel && (
                    <StyledBasicButton
                        isVisible={!!stepIndex}
                        isPrimary={false}
                        name={'Back'}
                        isOutlined
                        action={handleBack}
                        style={customBackButtonStyle}
                    />
                )}
                {stepIndex !== mappedSteps.length - 1 ? (
                    <BasicButton
                        name={nextButtonLabel}
                        action={handleNext}
                        disabled={isNextButtonDisabled || disableAllAction}
                        style={customNextButtonStyle}
                    />
                ) : (
                    rightActionButtonsForLastStep ?? null
                )}
            </HeaderActionContainer>
        );

    return (
        <AampeStepperContainer className={className}>
            <StepperHeaderRow>
                <AampeSteps
                    dividerType={dividerType}
                    steps={mappedSteps || []}
                    currentStepIndex={stepIndex}
                    onClick={handleStepClick}
                    showStepsAsCompleted={showStepsAsCompleted}
                    stepsStyle={stepsStyle}
                    dividerWidth={dividerWidth}
                />
                {headerActionButtons}
            </StepperHeaderRow>
            {dividerType === 'vertical' && <Divider type="vertical" />}
            <StepperContainer>
                {customStepperBodyHeader && customStepperBodyHeader}
                <Container
                    id={containerId}
                    isCoverStep={isCoverStep}
                    style={containerStyle}
                >
                    <HeaderAndContentContainer>
                        {!mappedSteps[stepIndex].hideHeader && (
                            <HeaderContainer>
                                <StepHeaderWithDescription>
                                    <AampeStepperHeading>
                                        {mappedSteps[stepIndex].name ||
                                            mappedSteps[stepIndex].title}
                                    </AampeStepperHeading>
                                    {mappedSteps[stepIndex]?.description && (
                                        <StepDescription>
                                            {
                                                mappedSteps[stepIndex]
                                                    ?.description
                                            }
                                        </StepDescription>
                                    )}
                                </StepHeaderWithDescription>

                                {mappedSteps[stepIndex].renderAction}
                            </HeaderContainer>
                        )}
                        <ContentContainer style={contentContainerStyle}>
                            {mappedSteps[stepIndex].renderComponent}
                        </ContentContainer>
                    </HeaderAndContentContainer>

                    {!actionsInHeader &&
                        !mappedSteps[stepIndex].hideActionButtons && (
                            <ActionContainer>
                                <StyledBasicButton
                                    isVisible={!!stepIndex}
                                    isPrimary={false}
                                    name={'Back'}
                                    isOutlined
                                    action={handleBack}
                                    style={customBackButtonStyle}
                                />
                                <StyledBasicButton
                                    isVisible={
                                        stepIndex !== mappedSteps.length - 1
                                    }
                                    name={'Next'}
                                    action={handleNext}
                                    disabled={
                                        isNextButtonDisabled || disableAllAction
                                    }
                                    style={customNextButtonStyle}
                                />
                                {stepIndex === mappedSteps.length - 1 &&
                                    rightActionButtonsForLastStep && (
                                        <>{rightActionButtonsForLastStep}</>
                                    )}
                            </ActionContainer>
                        )}
                </Container>
            </StepperContainer>
        </AampeStepperContainer>
    );
};

export default AampeStepperComponent;

const AampeStepperContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(5),
});

const StepperHeaderRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: aampeTheme.spacing4(4),
});

const HeaderActionContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    flexShrink: 0,
});

const StepperContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
});

const Container = styled.div.withConfig({
    shouldForwardProp: (prop) => !['isCoverStep'].includes(prop),
})<StyledProps>(({ isCoverStep }) => ({
    display: 'flex',
    width: '100%',
    alignItems: 'start',
    flexDirection: 'column',
    backgroundColor: aampeTheme.colors.white,
    borderRadius: aampeTheme.borderRadius.default,
    padding: isCoverStep ? 0 : aampeTheme.spacing4(6),
    gap: aampeTheme.spacing4(4),
    justifyContent: 'space-between',
}));

const ActionContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
});

const StyledBasicButton = styled(BasicButton).withConfig({
    shouldForwardProp: (prop) => !['isVisible'].includes(prop),
})<StyledProps>(({ isVisible }) => ({
    visibility: isVisible ? 'visible' : 'hidden',
}));

const AampeStepperHeading = styled.span({
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '28px',
    color: aampeTheme.colors.gray800,
});

const ContentContainer = styled.div({
    width: '100%',
});

const HeaderContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
});

const HeaderAndContentContainer = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
});

const StepHeaderWithDescription = styled.div({
    display: 'flex',
    flexDirection: 'column',
    rowGap: aampeTheme.spacing4(2),
});

const StepDescription = styled.span({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
    color: aampeTheme.colors.textAccessibleWG01,
});
